import React, {PureComponent} from "react"
import moment from "moment"

import {Body, H2, Label} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import Spacing from "@irati/ui/src/Spacing"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Flex from "@irati/ui/src/Flex"
import theme from "@irati/ui/src/global/theme"
import Loading from "@irati/ui/src/Loading"
import workosService from "@irati/workos/services/workos"
import authentication from "@irati/auth"
import adminTracking from "@irati-tracking/admin"

import {LinkCard, StyledLink} from "./styled"

interface Props {
  isSSO?: boolean,
  isDisabled: boolean
}

interface State {
  generateAgain: boolean,
  isCopied: boolean,
  isToday: boolean,
  expiryDate: string,
  link: string,
  load: Promise<any> | null
}

class GenerateWorkOSLinks extends PureComponent<Props, State> {
  state: State = {
    generateAgain: false,
    isCopied: false,
    isToday: true,
    expiryDate: "",
    link: "",
    load: null
  }

  generateLinkCode = () => {
    this.setState({isCopied: false})
    const load = workosService.generateSetupLinkCode(authentication.business().uuid)
      .then((setupResponse) => {
        const expiryDate = moment.utc(setupResponse.expiresAt).local()
        const expiryDay = expiryDate.format("D")
        const today = moment().local().format("D")
        const link = `${window.location.origin}/workOS-integrations-link?code=${setupResponse.code}`
        this.setState({link, expiryDate: expiryDate.format("HH:mm"), isToday: expiryDay === today, generateAgain: true})
        this.addMixpanelEvents()
      })
    this.setState({load})
  }

  copyLink = async() => {
    try {
      await navigator.clipboard.writeText(this.state.link).then(() => {
        this.setState({isCopied: true})
        this.addMixpanelEvents(true)
      })
    } catch (err) {
      console.error("Failed to copy: ", err)
    }
  }

  addMixpanelEvents = (copyLink?: boolean) => {
    if (this.props.isSSO) {
      return copyLink ? adminTracking.copyLinkWorkOSIntegrationsSSO() : adminTracking.triggerWorkOSIntegrationsSSO()
    } else {
      return copyLink ? adminTracking.copyLinkWorkOSIntegrationsDsync() : adminTracking.triggerWorkOSIntegrationsDsync()
    }
  }

  render() {
    const {isSSO, isDisabled} = this.props
    const {generateAgain, link, isCopied, expiryDate, isToday, load} = this.state

    return (
      <Spacing marginTop={isSSO ? 0: 7} marginBottom={isSSO ? 0: 7}>
        <H2 marginRight={1} marginBottom={1}>
          {isSSO
            ? <FormattedMessage id="admin.components.integration.generateWorkOSLinks.sso.title" />
            : <FormattedMessage id="admin.components.integration.generateWorkOSLinks.dsync.title" />
          }
        </H2>
        <Body marginBottom={3}>
          {isSSO
            ? <FormattedMessage id="admin.components.integration.generateWorkOSLinks.sso.body" />
            : <FormattedMessage id="admin.components.integration.generateWorkOSLinks.dsync.body" />
          }
        </Body>
        {generateAgain && <Loading load={load}>
          <LinkCard alignItems="center" justifyContent="space-between">
            <StyledLink marginLeft={2}>{link}</StyledLink>
            <Button marginRight={2} onClick={this.copyLink} primary small>
              {!isCopied
                ? <FormattedMessage id="admin.components.integration.generateWorkOSLinks.button.copyLink" />
                : <FormattedMessage id="admin.components.integration.generateWorkOSLinks.button.isCopied" />
              }
              <Icon icon={IconTypes.files} color={theme.colors.utility.white} marginLeft={1} size={14} />
            </Button>
          </LinkCard>
          <Flex marginTop={1} marginBottom={3}>
            <Spacing marginLeft={2}>
              <Icon color={theme.colors.tertiary.o_400} icon={IconTypes.circleAlert} size={20} />
            </Spacing>
            <Body marginLeft={1} color={theme.colors.tertiary.o_400}>
              {isToday
                ? <FormattedMessage
                  id="admin.components.integration.generateWorkOSLinks.linkExpire"
                  values={{today: <Label color={theme.colors.tertiary.o_400} large weight="bold">
                    <FormattedMessage
                      id="admin.components.integration.generateWorkOSLinks.expireToday"
                      values={{time: expiryDate}} />
                  </Label>
                  }} />
                : <FormattedMessage
                  id="admin.components.integration.generateWorkOSLinks.linkExpire"
                  values={{today: <Label color={theme.colors.tertiary.o_400} large weight="bold">
                    <FormattedMessage
                      id="admin.components.integration.generateWorkOSLinks.expireTomorrow"
                      values={{time: expiryDate}} />
                  </Label>
                  }} />}
            </Body>
          </Flex>
        </Loading>}
        <Button onClick={this.generateLinkCode} disabled={isDisabled} primary>
          {!generateAgain
            ? <FormattedMessage id="admin.components.integration.generateWorkOSLinks.button.generate" />
            : <FormattedMessage id="admin.components.integration.generateWorkOSLinks.button.generateAgain" />
          }
        </Button>
      </Spacing>
    )
  }
}

export default GenerateWorkOSLinks
